import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BrandLogo } from '@/components/ui/brand-logo'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useOrganizations } from '@/hooks/useOrganizations'
import { useWorkspaceSwitch } from '@/hooks/useWorkspaceSwitch'
import { routePaths } from '@/lib/router'
import { useAuthStore } from '@/stores/auth'

// Sidebar-header workspace switcher. The dedicated workspaces page remains the
// management surface; this menu keeps switching close to the navigation.
export function OrgSwitcher() {
  const organization = useAuthStore((state) => state.organization)
  const { switchWorkspace, isSwitching } = useWorkspaceSwitch()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { organizations: workspaces } = useOrganizations()
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const filteredWorkspaces = workspaces.filter(
    (workspace) =>
      workspace.name.toLowerCase().includes(normalizedSearchQuery) ||
      workspace.domain?.toLowerCase().includes(normalizedSearchQuery)
  )

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)

    if (!nextOpen) setSearchQuery('')
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent"
              role="combobox"
              aria-label={`Switch workspace, current workspace: ${organization?.name ?? 'Workspace'}`}
              aria-expanded={open}
            >
              <div className="size-6 shrink-0 overflow-hidden [&_img]:size-6">
                <BrandLogo />
              </div>

              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-semibold">
                  {organization?.name ?? 'Workspace'}
                </span>
              </div>

              <ChevronsUpDown className="ml-auto size-4 shrink-0" />
            </SidebarMenuButton>
          </PopoverTrigger>

          <PopoverContent align="start" className="w-64 p-0">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search workspaces..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />

              <CommandList>
                {filteredWorkspaces.length === 0 ? (
                  <CommandEmpty>No workspaces found.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {filteredWorkspaces.map((workspace) => (
                      <CommandItem
                        key={workspace.id}
                        value={workspace.id}
                        disabled={isSwitching}
                        aria-current={
                          workspace.id === organization?.id ? 'true' : undefined
                        }
                        onSelect={() => {
                          handleOpenChange(false)
                          void switchWorkspace(workspace)
                        }}
                        className="cursor-pointer"
                      >
                        <span className="truncate">{workspace.name}</span>
                        {workspace.id === organization?.id && (
                          <Check className="ml-auto size-4" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>

              <div className="border-t p-1">
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start rounded-sm"
                >
                  <Link
                    to={routePaths.workspaces}
                    onClick={() => handleOpenChange(false)}
                  >
                    <Building2 className="mr-2 size-4" />
                    Manage workspaces
                  </Link>
                </Button>
              </div>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
